/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { ActionKernelType, ActionType } from 'client-code/actions';

import {
    DEFAULT_SEEK_RANGE_IN_MILLISECONDS,
    HALF_HOUR_IN_MILLISECONDS,
    MILLISECONDS_IN_A_MINUTE,
    MILLISECONDS_IN_A_SECOND,
    MIN_CACHE_IN_MILLISECONDS,
    TICK_DURATION,
} from '../../../config';
import { AppUtils } from '../../AppUtils';
import { PlaybackContentType } from '../../playback/enums';
import { IMetadata, IPlayable, IPlaybackIntervalInfo, ITrickplay } from '../interfaces';

export class TrickplayImpl implements ITrickplay {
    private mMetaData: IMetadata;

    private mPlayable: IPlayable | null;

    private mPlaybackIntervalInfo: IPlaybackIntervalInfo;

    public constructor(
        mPlayable: IPlayable | null,
        mPlaybackIntervalInfo: IPlaybackIntervalInfo,
        mMetaData: IMetadata,
    ) {
        this.mPlayable = mPlayable;
        this.mPlaybackIntervalInfo = mPlaybackIntervalInfo;
        this.mMetaData = mMetaData;
    }

    /**
     * This function to get tick mark duration in minutes from program duration
     * @param duration number
     * @returns number
     */
    public getTickmarkDuration(duration: number): number {
        const threeHours = 3 * 4 * TICK_DURATION;
        const sixHours = 6 * 4 * TICK_DURATION;

        if (duration >= TICK_DURATION && duration < threeHours) {
            return TICK_DURATION;
        }
        if (duration >= threeHours && duration <= sixHours) {
            return 2 * TICK_DURATION;
        }
        if (duration > sixHours) {
            return 4 * TICK_DURATION;
        }
        return TICK_DURATION;
    }

    /**
     * This function to calculate program duration in seconds
     * @returns number
     */
    public getProgramDuration(): number {
        let duration;
        const { start, end } = this.mPlaybackIntervalInfo.seekRange;
        const showingDuration =
            this.mMetaData.watchAction?.kernel?.duration ??
            this.mMetaData.offerPreview?.contentPreview?.recordingDuration ??
            this.mMetaData.offerPreview?.contentPreview?.duration ??
            0;
        const durationFromPlayer = end - start;
        if (
            this.mPlayable?.playbackContentType === PlaybackContentType.STARTOVER_BEGINNING ||
            this.mPlayable?.playbackContentType === PlaybackContentType.STARTOVER_RESUME ||
            this.isNdvrLive() ||
            this.isLiveWithRecording()
        ) {
            duration = Math.max(showingDuration, durationFromPlayer);
        } else if (
            this.mPlayable?.playbackContentType === PlaybackContentType.CATCHUP_BEGINNING ||
            this.mPlayable?.playbackContentType === PlaybackContentType.CATCHUP_RESUME ||
            this.isNdvrRecorded()
        ) {
            duration = durationFromPlayer;
        } else {
            const cacheSize =
                durationFromPlayer * MILLISECONDS_IN_A_SECOND || DEFAULT_SEEK_RANGE_IN_MILLISECONDS;
            let finalCacheSize =
                cacheSize <= MIN_CACHE_IN_MILLISECONDS ? HALF_HOUR_IN_MILLISECONDS : cacheSize;
            /*
             * Rounding off the finalCacheSize to perfect minutes, so that few seconds or
             * milliseconds would not add extra 30 minutes to the endTime due its
             * Math.ceil calculation
             */
            finalCacheSize =
                Math.trunc(finalCacheSize / MILLISECONDS_IN_A_MINUTE) * MILLISECONDS_IN_A_MINUTE;

            // Subtract CACHE_SIZE from the current time and round DOWN to the nearest half.
            let startTime = Date.now() - finalCacheSize;
            startTime =
                Math.floor(startTime / HALF_HOUR_IN_MILLISECONDS) * HALF_HOUR_IN_MILLISECONDS;

            // Start Time plus CACHE_SIZE plus 30 minutes, rounded UP to the nearest half hour.
            let endTime = startTime + finalCacheSize + HALF_HOUR_IN_MILLISECONDS;
            endTime = Math.ceil(endTime / HALF_HOUR_IN_MILLISECONDS) * HALF_HOUR_IN_MILLISECONDS;
            duration = (endTime - startTime) / MILLISECONDS_IN_A_SECOND;
        }
        return duration;
    }

    private getWatchAction(): any {
        const watchAction = this.mMetaData.actionList?.actions?.find((action) => {
            return (
                action.actionType === ActionType.WatchNowAction &&
                (action.kernel.actionKernelType === ActionKernelType.WatchFromMyShowsActionKernel ||
                    action.kernel.actionKernelType ===
                        ActionKernelType.ResumeFromMyShowsActionKernel)
            );
        });
        return watchAction;
    }

    private isNdvrLive(): boolean {
        return (
            (this.mPlayable?.playbackContentType === PlaybackContentType.NDVR_LIVE ||
                this.mPlayable?.playbackContentType === PlaybackContentType.NDVR_RECORDED) &&
            this.isRecordingInProgress()
        );
    }

    private isNdvrRecorded(): boolean {
        return (
            (this.mPlayable?.playbackContentType === PlaybackContentType.NDVR_LIVE ||
                this.mPlayable?.playbackContentType === PlaybackContentType.NDVR_RECORDED) &&
            !this.isRecordingInProgress()
        );
    }

    private isRecordingInProgress(): boolean {
        let isRecordingInProgress: boolean = false;
        if (
            !AppUtils.isEmpty(this.getWatchAction()) &&
            this.getWatchAction().kernel.isRecordingInProgress
        ) {
            isRecordingInProgress = this.getWatchAction().kernel.isRecordingInProgress;
        }
        return isRecordingInProgress;
    }

    private isLiveWithRecording(): boolean {
        let hasRecording: boolean = false;
        if (!AppUtils.isEmpty(this.getWatchAction()) && this.getWatchAction().hasRecording) {
            hasRecording = this.getWatchAction().hasRecording;
        }
        return this.mPlayable?.playbackContentType === PlaybackContentType.LIVE && hasRecording;
    }
}
